import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, InputGroup, Button, Badge } from 'react-bootstrap';
import PageTitle from '../../common/PageTitle';
import { apiListUsers } from '../../../api/api';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../context/ToastContext';
import './SettingsUsers.css';
import { uSubscriptionName, uSubscriptionExpiration, uSearchesRemaining } from '../../../utils';
import FormattedMobile from '../../common/FormattedMobile';

const SettingsUsers = () => {
  const { user } = useUser();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersFree, setUsersFree] = useState([]);
  const [usersPaid, setUsersPaid] = useState([]);
  const [usersLast, setUsersLast] = useState([]);
  const [usersExpired, setUsersExpired] = useState([]);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const listUsers = async () => {
      if (!user) {
        addToast('Usuário não autenticado');
        return;
      }
      try {
        const response = await apiListUsers();
        if (!response) {
          addToast('Erro ao carregar usuários');
          return;
        }
        setUsers(response);
        setFilteredUsers(response);
      } catch (error) {
        addToast('Erro ao carregar usuários');
      }
    };
    listUsers();
  }, [user]);

  useEffect(() => {
    const daysDiff = (date) => {
      if (!date) {
        return 0;
      }
      const dt = new Date(date);
      const now = new Date();
      const diff = now - dt;
      return diff / (1000 * 60 * 60 * 24);
    };

    if (users) {
      const free = users.filter((u) => u.subscription === 0);
      const paid = users.filter((u) => u.subscription > 0);
      const last = users.filter((u) => daysDiff(u.createdAt) < 30);
      const expired = users.filter((u) => daysDiff(u.releasedDeadline) > 0);
      setUsersFree(free);
      setUsersPaid(paid);
      setUsersLast(last);
      setUsersExpired(expired);
    }
  }, [users]);

  const handleFilter = (e) => {
    const value = e.target.value.toLowerCase();
    const filtered = users.filter((u) => {
      let found = false;
      if (u.name)
        found = found || u.name.toLowerCase().includes(value);
      if (u.email)
        found = found || u.email.toLowerCase().includes(value);
      if (u.state)
        found = found || u.state.toLowerCase().includes(value);
      if (u.city)
        found = found || u.city.toLowerCase().includes(value);
      return found;
    });
    setFilteredUsers(filtered);
  };

  const handleClear = () => {
    setFilteredUsers(users);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const userLastLogin = (date) => {
    if (!date) {
      return <Badge pill bg='danger'>nunca entrou</Badge>;
    }

    const lastDate = new Date(date).toLocaleDateString();

    const now = new Date();
    const last = new Date(date);
    const diff = last - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return <Badge pill bg='success'>{lastDate}</Badge>;
    } else if (days > -30) {
      return <span>{lastDate} <Badge pill bg='info'>{days} d</Badge></span>;
    } else {
      return <span>{lastDate} <Badge pill bg='warning'>{days} d</Badge></span>;
    }
  };

  const handleEditUser = (userId) => {
    navigate(`/main/profile/${userId}`);
  };

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <PageTitle text='usuários' />
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <Card.Header className='bg-secondary text-white'>Usuários</Card.Header>
              <Card.Body>
                <h1 className='app-text-orange text-center'>
                  <i className='bi bi-people-fill me-2'></i>{users && users.length}</h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">total de usuários</Card.Footer>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Header className='bg-secondary text-white'>Usuários</Card.Header>
              <Card.Body>
                <h1 className='app-text-orange text-center'>
                  <i className='bi bi-people me-2'></i>{usersLast && usersLast.length}</h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">novos usuários (30 dias)</Card.Footer>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Header className='bg-secondary text-white'>Usuários</Card.Header>
              <Card.Body>
                <h1 className='app-text-orange text-center'><i className='bi bi-person-fill me-2'></i>{usersFree && usersFree.length}</h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">plano grátis</Card.Footer>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Header className='bg-secondary text-white'>Usuários</Card.Header>
              <Card.Body>
                <h1 className='app-text-orange text-center'><i className='bi bi-person-fill me-2'></i>{usersPaid && usersPaid.length}</h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">plano pago</Card.Footer>
            </Card>
          </Col>

          <Col>
            <Card>
              <Card.Header className='bg-secondary text-white'>Usuários</Card.Header>
              <Card.Body>
                <h1 className='text-danger text-center'><i className='bi bi-person-fill me-2'></i>{usersExpired && usersExpired.length}</h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">expirados</Card.Footer>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col className='mt-2' sm={4}>
            <Form.Label htmlFor="settingsSearch">Pesquisar usuários</Form.Label>
            <InputGroup className="mb-3">
              <Form.Control ref={searchInputRef} type="text" size="sm" placeholder='Nome, e-mail, login, UF, cidade, ...' onChange={(e) => handleFilter(e)} />
              <Button variant="secondary" id="button-addon2" size="sm" onClick={handleClear}>
                Limpar
              </Button>
            </InputGroup>
          </Col>
        </Row>
        <Row>
          <Col className='mt-2'>
            <Table size='sm' className='small-font-table' striped hover>
              <thead>
                <tr>
                  <th>Criação</th>
                  <th>Atualização</th>
                  <th>Login</th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>UF</th>
                  <th>Cidade</th>
                  <th>Plano</th>
                  <th>Vencimento</th>
                  <th>Pesquisas</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers && filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{new Date(u.createdAt).toLocaleDateString()} <Badge>{u.registerFrom}</Badge></td>
                    <td>{new Date(u.updatedAt).toLocaleDateString()}</td>
                    <td>{userLastLogin(u.loginDate)}</td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td><FormattedMobile mobile={u.mobile}/></td>
                    <td>{u.state}</td>
                    <td>{u.city}</td>
                    <td>{uSubscriptionName(u.subscription)}</td>
                    <td>{uSubscriptionExpiration(u.releasedDeadline)}</td>
                    <td>{uSearchesRemaining(u.searches)}</td>
                    <td><Button variant='secondary' size='sm' onClick={() => handleEditUser(u.id)} >Editar</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SettingsUsers;